import React, { useState, useMemo, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { cryptoPaymentApi } from '../../lib/supabase-api';

const AdminOrders: React.FC = () => {
  const { 
    orders, 
    updateOrder, 
    updateOrderStatus, 
    addTrackingNumber, 
    deleteOrder,
    // Trash-related functions
    moveOrderToTrash,
    trashOrders,
    loadTrashOrders 
  } = useAdmin();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState<string>('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'totalAmount' | 'status'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingForm, setTrackingForm] = useState({ number: '', company: '' });
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [cryptoDetail, setCryptoDetail] = useState<any>(null);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState<any>(null);
  // Trash Move Confirm Modal Status
  const [trashModalOpen, setTrashModalOpen] = useState(false);
  const [orderToTrash, setOrderToTrash] = useState<string | null>(null);
  // Permanent delete confirm modal (trash only)
  const [permanentDeleteModalOpen, setPermanentDeleteModalOpen] = useState(false);
  const [orderToDeletePermanently, setOrderToDeletePermanently] = useState<string | null>(null);
  // Order copy feedback
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Shipping carriers list (international dropshipping from India → global)
  const shippingCompanies = [
    'India Post EMS',
    'India Post Registered',
    'DHL Express',
    'FedEx International',
    'Aramex',
    'BlueDart',
  ];

  // Filtered and sorted Order
  const filteredAndSortedOrders = useMemo(() => {
    let filtered = orders.filter(order => {
      const matchesSearch = order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           order.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           order.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           order.items.some(item => item.productName.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = !filterStatus || order.status === filterStatus;
      const matchesPaymentStatus = !filterPaymentStatus || order.paymentStatus === filterPaymentStatus;
      return matchesSearch && matchesStatus && matchesPaymentStatus;
    });

    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'totalAmount':
          aValue = a.totalAmount;
          bValue = b.totalAmount;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [orders, searchTerm, filterStatus, filterPaymentStatus, sortBy, sortOrder]);

  const handleStatusChange = async (orderId: string, newStatus: 'payment_pending' | 'payment_confirmed' | 'shipping' | 'delivered' | 'cancelled' | 'refunded') => {
    if (window.confirm(`Change order status to '${getStatusLabel(newStatus)}'?`)) {
      await updateOrderStatus(orderId, newStatus);
    }
  };

  const handleAddTracking = (orderId: string) => {
    setSelectedOrder(orderId);
    const order = orders.find(o => o.id === orderId);
    setTrackingForm({
      number: order?.trackingNumber || '',
      company: order?.shippingCompany || ''
    });
    setTrackingModalOpen(true);
  };

  const handleSubmitTracking = async () => {
    if (!selectedOrder || !trackingForm.number.trim() || !trackingForm.company) return;

    await addTrackingNumber(selectedOrder, trackingForm.number.trim(), trackingForm.company);
    setTrackingModalOpen(false);
    setSelectedOrder(null);
    setTrackingForm({ number: '', company: '' });
  };

  const handleViewDetail = (order: any) => {
    setSelectedOrderDetail(order);
    setCryptoDetail(null);
    setDetailModalOpen(true);
    // Load crypto payment data if payment method is crypto
    if (order.payment_method === 'crypto' || order.paymentMethod === 'crypto') {
      cryptoPaymentApi.getByOrderId(order.id).then(data => {
        if (data) setCryptoDetail(data);
      });
    }
  };

  // MyMemory Translation API (free)
  const translateToEnglish = async (text: string): Promise<string> => {
    if (!text.trim()) return '';
    try {
      const res = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=ko|en`
      );
      const data = await res.json();
      if (data.responseStatus === 200 && data.responseData?.translatedText) {
        return data.responseData.translatedText;
      }
      return text;
    } catch {
      return text;
    }
  };

  // productId to English product name (e.g. "tastilia-5mg" → "tastilia 5mg")
  const productIdToEnglishName = (productId: string): string => {
    if (!productId) return '';
    // "tastilia-5mg-oral-jelly" → "tastilia 5mg oral jelly"
    // Remove hyphens between numbers+units, rest to spaces
    return productId
      .replace(/-(\d)/g, ' $1') // "tastilia-5mg" → "tastilia 5mg"
      .replace(/-/g, ' ')       // remaining hyphens to spaces
      .trim();
  };

  // Copy English order data to clipboard (translate missing data on the fly)
  const [isCopying, setIsCopying] = useState(false);
  const handleCopyOrderData = async (order: any) => {
    setIsCopying(true);
    try {
      const addr = typeof order.shippingAddress === 'object' ? order.shippingAddress : {};
      const zipCode = addr.zipCode || '-';
      const phone = addr.phone || '-';

      // 1. English name: use saved value if available, otherwise translate
      let nameEng = addr.nameEnglish;
      if (!nameEng) {
        const korName = addr.name || order.userName || '';
        nameEng = korName ? await translateToEnglish(korName) : '-';
      }

      // 2. English address: use saved value if available, otherwise translate.
      //    Strip any legacy "Republic of Korea" suffix but do not add it back —
      //    country is no longer hardcoded (global English site).
      let fullAddrEng = addr.fullAddressEnglish;
      if (!fullAddrEng) {
        const korAddr = [addr.detailAddress, addr.address].filter(Boolean).join(' ');
        if (korAddr) {
          const translated = await translateToEnglish(korAddr);
          const clean = translated.replace(/,?\s*Republic of Korea\s*$/i, '').trim();
          const parts = [clean, zipCode !== '-' ? zipCode : ''].filter(Boolean);
          fullAddrEng = parts.join(', ');
        } else {
          fullAddrEng = '-';
        }
      }
      // Strip any stray "Republic of Korea" from legacy saved addresses
      fullAddrEng = fullAddrEng.replace(/,?\s*Republic of Korea\s*/gi, '').replace(/,\s*,/g, ',').replace(/,\s*$/, '').trim();

      // 3. Product: extract English name from productId + pillQuantity*quantity tablets
      const orderLine = order.items.map((item: any) => {
        const pillQty = item.pillQuantity || 0;
        const totalTablets = pillQty * item.quantity;
        const engName = productIdToEnglishName(item.productId);
        return totalTablets > 0
          ? `${engName} ${totalTablets} tablets`
          : `${engName} x${item.quantity}`;
      }).join(', ');

      const text = [
        `Name - ${nameEng}`,
        `Address - ${fullAddrEng}`,
        `Post Code - ${zipCode}`,
        `Phone Number - ${phone}`,
        `Order - ${orderLine}`
      ].join('\n');

      await navigator.clipboard.writeText(text);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      console.error('Copy Failed:', err);
      alert('Failed to copy.');
    } finally {
      setIsCopying(false);
    }
  };

  // Trash move handlers
  const handleMoveToTrash = (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setOrderToTrash(orderId);
      setTrashModalOpen(true);
    }
  };

  // Permanent delete handler (trash only)
  const handlePermanentDelete = (orderId: string) => {
    const order = trashOrders.find(o => o.id === orderId);
    if (order) {
      setOrderToDeletePermanently(orderId);
      setPermanentDeleteModalOpen(true);
    }
  };

  // Trash Move Process
  const handleConfirmMoveToTrash = async () => {
    if (!orderToTrash) return;
    
    try {
      console.log('🗑️ Moving order to trash:', orderToTrash);
      const success = await moveOrderToTrash(orderToTrash);
      
      if (success) {
        console.log('✅ Trash Move Success');
        alert('Order has been moved to trash.');
      } else {
        console.error('❌ Trash Move Failed');
        alert('Failed to move to trash. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error moving to trash:', error);
      alert('An error occurred while moving to trash.');
    } finally {
      setTrashModalOpen(false);
      setOrderToTrash(null);
    }
  };

  // Permanent delete process (trash only)
  const handleConfirmPermanentDelete = async () => {
    if (!orderToDeletePermanently) return;
    
    try {
      console.log('🔥 Permanent order delete request:', orderToDeletePermanently);
      const success = await deleteOrder(orderToDeletePermanently);
      
      if (success) {
        console.log('✅ Order Permanent Delete Success');
        alert('Order has been permanently deleted.');
      } else {
        console.error('❌ Order Permanent Delete Failed');
        alert('Failed to permanently delete. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error permanently deleting order:', error);
      alert('An error occurred during permanent deletion.');
    } finally {
      setPermanentDeleteModalOpen(false);
      setOrderToDeletePermanently(null);
    }
  };

  // Cancel handlers (unused but kept for future reference) - disabled
  /*
  const handleCancelDelete = () => {
    // This function is no longer used - replaced by trash system
  };
  */

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US').format(price);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'payment_pending': return 'Payment Pending';
      case 'payment_confirmed': return 'Payment Confirmed';
      case 'shipping': return 'Shipping';
      case 'delivered': return 'Delivered';
      case 'cancelled': return 'Cancelled';
      case 'refunded': return 'Refunded';
      default: return status;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'payment_pending':
        return { label: getStatusLabel(status), color: 'bg-yellow-100 text-yellow-800' };
      case 'payment_confirmed':
        return { label: getStatusLabel(status), color: 'bg-forest-100 text-forest-800' };
      case 'shipping':
        return { label: getStatusLabel(status), color: 'bg-orange-100 text-orange-800' };
      case 'delivered':
        return { label: getStatusLabel(status), color: 'bg-green-100 text-green-800' };
      case 'cancelled':
        return { label: getStatusLabel(status), color: 'bg-red-100 text-red-800' };
      case 'refunded':
        return { label: getStatusLabel(status), color: 'bg-neutral-100 text-neutral-800' };
      default:
        return { label: status, color: 'bg-neutral-100 text-neutral-800' };
    }
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return { label: 'Payment Pending', color: 'bg-yellow-100 text-yellow-800' };
      case 'paid':
        return { label: 'Paid', color: 'bg-green-100 text-green-800' };
      case 'failed':
        return { label: 'Payment Failed', color: 'bg-red-100 text-red-800' };
      case 'refunded':
        return { label: 'Refunded', color: 'bg-neutral-100 text-neutral-800' };
      default:
        return { label: status, color: 'bg-neutral-100 text-neutral-800' };
    }
  };

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'payment_pending').length,
    confirmed: orders.filter(o => o.status === 'payment_confirmed').length,
    shipped: orders.filter(o => o.status === 'shipping').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
    totalRevenue: orders.filter(o => o.paymentStatus === 'paid').reduce((sum, o) => sum + o.totalAmount, 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Order/Shipping Management</h1>
          <p className="mt-2 text-sm text-neutral-700">
            {orders.length} order(s) registered in total.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-forest-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">All Orders</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.total}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">Process Pending</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.pending + stats.confirmed}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2v0M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">Shipping</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.shipped}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">Delivered</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.delivered}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-neutral-900">Revenue Overview</h3>
              <div className="mt-2 flex items-baseline">
                <p className="text-2xl font-semibold text-neutral-900">${formatPrice(stats.totalRevenue)}</p>
                <p className="ml-2 text-sm text-neutral-500">Total Revenue</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-neutral-500">Avg. Order Amount</div>
              <div className="text-lg font-medium text-neutral-900">
                ${formatPrice(stats.total > 0 ? Math.round(stats.totalRevenue / stats.total) : 0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-neutral-700 mb-1">
                Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-md leading-5 bg-white placeholder-neutral-500 focus:outline-none focus:placeholder-neutral-400 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                  placeholder="Search by order number, customer name, product..."
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="status-filter" className="block text-sm font-medium text-neutral-700 mb-1">
                Order Status
              </label>
              <select
                id="status-filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
              >
                <option value="">All Status</option>
                <option value="payment_pending">Payment Pending</option>
                <option value="payment_confirmed">Payment Confirmed</option>
                <option value="shipping">Shipping</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refund</option>
              </select>
            </div>

            {/* Payment Status Filter */}
            <div>
              <label htmlFor="payment-filter" className="block text-sm font-medium text-neutral-700 mb-1">
                Payment Status
              </label>
              <select
                id="payment-filter"
                value={filterPaymentStatus}
                onChange={(e) => setFilterPaymentStatus(e.target.value)}
                className="block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
              >
                <option value="">All</option>
                <option value="pending">Payment Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Payment Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label htmlFor="sort" className="block text-sm font-medium text-neutral-700 mb-1">
                Sort
              </label>
              <div className="flex space-x-2">
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                >
                  <option value="createdAt">Order Date</option>
                  <option value="totalAmount">Order Amount</option>
                  <option value="status">Status</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-neutral-300 rounded-md shadow-sm bg-white hover:bg-neutral-50 focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredAndSortedOrders.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-neutral-900">No orders found</h3>
            <p className="mt-1 text-sm text-neutral-500">Waiting for new orders.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Order Info
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Customer Info
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Order Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Shipping Info
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredAndSortedOrders.map((order) => {
                  const statusBadge = getStatusBadge(order.status);
                  const paymentBadge = getPaymentStatusBadge(order.paymentStatus);
                  return (
                    <tr key={order.id} className="hover:bg-neutral-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-neutral-900">
                            {order.orderNumber}
                          </div>
                          <div className="text-sm text-neutral-500">
                            {formatDate(order.createdAt)}
                          </div>
                          <div className="text-sm text-neutral-500">
                            {order.items.length} item(s)
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-neutral-900">
                            {order.userName}
                          </div>
                          <div className="text-sm text-neutral-500">
                            {order.userEmail}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-neutral-900">
                          ${formatPrice(order.totalAmount)}
                        </div>
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${paymentBadge.color}`}>
                          {paymentBadge.label}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusChange(order.id, e.target.value as any)}
                          className={`text-sm border border-neutral-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-forest-500 focus:border-forest-500 ${statusBadge.color.replace('bg-', 'bg-opacity-10 border-').replace('text-', 'text-')}`}
                        >
                          <option value="payment_pending">Payment Pending</option>
                          <option value="payment_confirmed">Payment Confirmed</option>
                          <option value="shipping">Shipping</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="refunded">Refund</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {order.trackingNumber ? (
                          <div>
                            <div className="text-sm font-medium text-neutral-900">
                              {order.shippingCompany}
                            </div>
                            <div className="text-sm text-neutral-500 font-mono">
                              {order.trackingNumber}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-neutral-500">No tracking info</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewDetail(order)}
                            className="text-forest-400 hover:text-forest-900"
                          >
                            Details
                          </button>
                          <button
                            onClick={() => handleAddTracking(order.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Tracking
                          </button>
                          <button
                            onClick={() => handleMoveToTrash(order.id)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Move to Trash"
                          >
                            Trash
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tracking Modal */}
      {trackingModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-neutral-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2v0M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-neutral-900">
                    Register Tracking Number
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-neutral-500">
                      Please enter the shipping carrier and tracking number.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Shipping Carrier</label>
                  <select
                    value={trackingForm.company}
                    onChange={(e) => setTrackingForm(prev => ({ ...prev, company: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                  >
                    <option value="">Select a shipping carrier</option>
                    {shippingCompanies.map(company => (
                      <option key={company} value={company}>{company}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Tracking Number</label>
                  <input
                    type="text"
                    value={trackingForm.number}
                    onChange={(e) => setTrackingForm(prev => ({ ...prev, number: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                    placeholder="Enter tracking number"
                  />
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleSubmitTracking}
                  disabled={!trackingForm.number.trim() || !trackingForm.company}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-forest-400 text-base font-medium text-white hover:bg-forest-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTrackingModalOpen(false);
                    setSelectedOrder(null);
                    setTrackingForm({ number: '', company: '' });
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-neutral-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {detailModalOpen && selectedOrderDetail && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-neutral-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-neutral-900">
                    Order Details
                  </h3>
                  <button
                    onClick={() => setDetailModalOpen(false)}
                    className="rounded-md text-neutral-400 hover:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-forest-500"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Order Info */}
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-neutral-900 mb-3">Order Info</h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-neutral-500">Order Number</dt>
                        <dd className="text-sm text-neutral-900 font-mono">{selectedOrderDetail.orderNumber}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-neutral-500">Order Date</dt>
                        <dd className="text-sm text-neutral-900">{formatDate(selectedOrderDetail.createdAt)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-neutral-500">Order Status</dt>
                        <dd>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(selectedOrderDetail.status).color}`}>
                            {getStatusBadge(selectedOrderDetail.status).label}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-neutral-500">Payment Status</dt>
                        <dd>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusBadge(selectedOrderDetail.paymentStatus).color}`}>
                            {getPaymentStatusBadge(selectedOrderDetail.paymentStatus).label}
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Crypto Payment Details */}
                  {cryptoDetail && (
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 col-span-2">
                      <h4 className="text-sm font-medium text-amber-900 mb-3">Crypto Payment</h4>
                      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <dt className="text-xs font-medium text-amber-700">Coin</dt>
                          <dd className="text-sm text-amber-900 font-semibold">{cryptoDetail.coin}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-amber-700">Amount</dt>
                          <dd className="text-sm text-amber-900 font-mono">{cryptoDetail.amountCrypto} {cryptoDetail.coin}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-amber-700">Status</dt>
                          <dd className="text-sm text-amber-900 font-semibold capitalize">{cryptoDetail.status}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-amber-700">Network</dt>
                          <dd className="text-sm text-amber-900">{cryptoDetail.network}</dd>
                        </div>
                        <div className="col-span-2 sm:col-span-4">
                          <dt className="text-xs font-medium text-amber-700">Address</dt>
                          <dd className="text-xs text-amber-900 font-mono break-all">{cryptoDetail.address}</dd>
                        </div>
                      </dl>
                    </div>
                  )}

                  {/* Customer Info */}
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-neutral-900 mb-3">Customer Info</h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-neutral-500">Customer Name</dt>
                        <dd className="text-sm text-neutral-900">{selectedOrderDetail.userName}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-neutral-500">Email</dt>
                        <dd className="text-sm text-neutral-900">{selectedOrderDetail.userEmail}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-neutral-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-neutral-900">Shipping Info</h4>
                    <button
                      onClick={() => handleCopyOrderData(selectedOrderDetail)}
                      disabled={isCopying}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                        copyFeedback
                          ? 'bg-green-100 text-green-800'
                          : isCopying
                          ? 'bg-neutral-400 text-white cursor-wait'
                          : 'bg-forest-400 text-white hover:bg-forest-500'
                      }`}
                    >
                      {copyFeedback ? 'Copied!' : isCopying ? 'Translating...' : 'Copy for Order'}
                    </button>
                  </div>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-xs font-medium text-neutral-500">Recipient</dt>
                      <dd className="text-sm text-neutral-900">
                        {(() => {
                          const addr = typeof selectedOrderDetail.shippingAddress === 'object' ? selectedOrderDetail.shippingAddress : null;
                          return addr?.name || selectedOrderDetail.userName || '-';
                        })()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-neutral-500">English Name</dt>
                      <dd className="text-sm text-forest-700 font-mono">
                        {(() => {
                          const addr = typeof selectedOrderDetail.shippingAddress === 'object' ? selectedOrderDetail.shippingAddress : null;
                          return addr?.nameEnglish || '-';
                        })()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-neutral-500">Phone</dt>
                      <dd className="text-sm text-neutral-900">
                        {(() => {
                          const addr = typeof selectedOrderDetail.shippingAddress === 'object' ? selectedOrderDetail.shippingAddress : null;
                          return addr?.phone || selectedOrderDetail.userPhone || '-';
                        })()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-neutral-500">Zip Code</dt>
                      <dd className="text-sm text-neutral-900 font-mono">
                        {(() => {
                          const addr = typeof selectedOrderDetail.shippingAddress === 'object' ? selectedOrderDetail.shippingAddress : null;
                          return addr?.zipCode || '-';
                        })()}
                      </dd>
                    </div>
                    <div className="md:col-span-2">
                      <dt className="text-xs font-medium text-neutral-500">Address</dt>
                      <dd className="text-sm text-neutral-900">
                        {(() => {
                          const addr = typeof selectedOrderDetail.shippingAddress === 'object' ? selectedOrderDetail.shippingAddress : null;
                          if (!addr) return <span className="text-neutral-400">No shipping info</span>;
                          return <>{addr.zipCode && `(${addr.zipCode}) `}{addr.address} {addr.detailAddress}</>;
                        })()}
                      </dd>
                    </div>
                    <div className="md:col-span-2">
                      <dt className="text-xs font-medium text-neutral-500">English Address</dt>
                      <dd className="text-sm text-forest-700 font-mono">
                        {(() => {
                          const addr = typeof selectedOrderDetail.shippingAddress === 'object' ? selectedOrderDetail.shippingAddress : null;
                          return addr?.fullAddressEnglish || '-';
                        })()}
                      </dd>
                    </div>
                    {selectedOrderDetail.trackingNumber && (
                      <>
                        <div>
                          <dt className="text-xs font-medium text-neutral-500">Carrier</dt>
                          <dd className="text-sm text-neutral-900">{selectedOrderDetail.shippingCompany || '-'}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-neutral-500">Tracking Number</dt>
                          <dd className="text-sm text-neutral-900 font-mono">{selectedOrderDetail.trackingNumber}</dd>
                        </div>
                      </>
                    )}
                  </dl>
                </div>

                {/* Order Items */}
                <div>
                  <h4 className="text-sm font-medium text-neutral-900 mb-3">Ordered Products</h4>
                  <div className="bg-neutral-50 rounded-lg overflow-hidden">
                    <table className="min-w-full">
                      <thead className="bg-neutral-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Product Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Tablets/Qty</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Unit Price</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-200">
                        {selectedOrderDetail.items.map((item: any, index: number) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-neutral-900">{item.productName}</td>
                            <td className="px-4 py-2 text-sm text-neutral-900">
                              {item.pillQuantity ? `${item.pillQuantity} tablets x ${item.quantity} box(es)` : `${item.quantity} item(s)`}
                            </td>
                            <td className="px-4 py-2 text-sm text-neutral-900">${formatPrice(item.price)}</td>
                            <td className="px-4 py-2 text-sm text-neutral-900">${formatPrice(item.price * item.quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-neutral-900 mb-3">Payment Info</h4>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-neutral-500">Subtotal</dt>
                      <dd className="text-sm text-neutral-900">
                        ${formatPrice(selectedOrderDetail.subtotal || selectedOrderDetail.items.reduce((sum: number, item: any) => sum + (item.price * item.quantity), 0))}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-neutral-500">Shipping Fee</dt>
                      <dd className="text-sm text-neutral-900">${formatPrice(selectedOrderDetail.shippingFee || 0)}</dd>
                    </div>
                    {(selectedOrderDetail.discount || selectedOrderDetail.discountAmount) > 0 && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-neutral-500">Discount</dt>
                        <dd className="text-sm text-red-600">-${formatPrice(selectedOrderDetail.discount || selectedOrderDetail.discountAmount || 0)}</dd>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-neutral-200 pt-2">
                      <dt className="text-sm font-medium text-neutral-900">Total Payment</dt>
                      <dd className="text-sm font-medium text-neutral-900">${formatPrice(selectedOrderDetail.totalAmount)}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trash Move Confirm Modal */}
      {trashModalOpen && orderToTrash && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-neutral-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                  <span className="text-2xl">🗑️</span>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-neutral-900">
                    Move to Trash
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-neutral-500">
                      Move this order to trash? It can be restored later.
                    </p>
                    <p className="text-sm text-neutral-500 mt-1">
                      Order ID: <span className="font-mono text-neutral-800">{orderToTrash}</span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleConfirmMoveToTrash}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  🗑️ Move to Trash
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTrashModalOpen(false);
                    setOrderToTrash(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-neutral-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;